import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import {
  Search,
  Building,
  BarChart3,
  Calendar,
  HeartHandshake,
  TrendingUp,
  Users,
  Smartphone,
  ArrowRight,
  Scissors,
  Sparkles,
  Dumbbell,
  Stethoscope,
  PawPrint,
  Car,
  SearchX,
  X,
} from 'lucide-react';
import FiltrosEmpresas, {
  FILTROS_VACIOS,
  RADIOS_KM,
  aplicarFiltros,
  subcategoriasDe,
  type FiltrosEmpresasValor,
} from '@/components/pages/shared/FiltrosEmpresas';
import { useBuscarEmpresas } from '@/components/hooks/useBuscarEmpresas';
import { useProfiles } from '@/components/hooks/useProfiles';
import {
  cargarPromocionActiva,
  type PromocionActiva,
} from '@/components/hooks/usePromocion';
import RecommendedCompanies from '@/components/pages/RecommendedCompanies';
import Footer from '@/components/pages/shared/Footer';
import LoginDialog from '@/components/pages/dialogs/LoginDialog';
import LocationDialog from '@/components/pages/shared/LocationDialog';
import ClientRegisterChoiceDialog from '@/components/pages/dialogs/ClientRegisterChoiceDialog';
import CompanyRegisterChoiceDialog from '@/components/pages/dialogs/CompanyRegisterChoiceDialog';
import HomeSEO from '@/components/seo/HomeSEO';
import HomeSEOAccordion from '@/components/seo/HomeSEOAccordion';
import CategoryCityLinks from '@/components/pages/CategoryCityLinks';
import MobileLoginScreen from '@/components/pages/mobile/MobileLoginScreen';
import HeroRotatingTitle from '@/components/ui/HeroRotatingTitle';
import { supabase } from '@/lib/supabaseClient';
import { rutaInicioPanel } from '@/components/utils/rutasPanel';

// Distancia de scroll (px) en la que el fondo del header se vuelve opaco.
// Tiene que ser corta: el hero ya no es fijo, así que su título empieza a
// pasar por debajo del header en cuanto se hace scroll. Con 650 px el header
// seguía transparente mientras el título se metía bajo los botones.
const HEADER_SCROLL_RANGE = 80;

// Píxeles de scroll sobre los que se funde el buscador del header, contados
// desde el instante en que el buscador del hero desaparece bajo el header.
const HEADER_SEARCH_FADE_PX = 40;

// Alto de la fila del buscador móvil que cuelga bajo el header (pt-1 + h-10
// + pb-3). El fondo del header se estira esa distancia para cubrirla.
const MOBILE_SEARCH_ROW_PX = 56;

// Ficha de la app en Google Play a la que apunta el badge de Android del hero.
const URL_GOOGLE_PLAY = 'https://play.google.com/store/apps/details?id=com.bookoll.app';

// Rango de scroll (px) sobre el que se calcula el efecto parallax del hero.
const PARALLAX_SCROLL_RANGE = 1200;

// Altura del hero fijo, en vh. Cámbiala aquí para hacerlo más alto/bajo
// (p.ej. 50 = mitad de pantalla, 100 = pantalla completa).
const HERO_HEIGHT_VH = 56;
const HERO_HEIGHT = `${HERO_HEIGHT_VH}vh`;

/**
 * Dropdown de resultados de búsqueda, renderizado vía Portal en document.body.
 *
 * Motivo: el desplegable vivía dentro del hero (`fixed`, `z-0`), mientras el
 * bloque de contenido inferior es `relative z-10`. Un z-index alto en el
 * propio desplegable (z-[9999]) solo compite DENTRO del contexto de
 * apilamiento del hero, así que al comparar hero vs. bloque inferior perdía
 * siempre y quedaba tapado. Al portalear a document.body, el desplegable
 * sale de ese contexto y se posiciona con `position: fixed` calculado a
 * partir del rect del input, quedando siempre por encima de todo.
 */
type SearchResultsPortalProps = {
  anchorRef: React.RefObject<HTMLElement>;
  visible: boolean;
  filtered: any[];
  onSelect: (empresa: any) => void;
  itemImgSize?: string;
  itemTextSize?: string;
  itemPadding?: string;
  itemGap?: string;
};

const SearchResultsPortal = ({
  anchorRef,
  visible,
  filtered,
  onSelect,
  itemImgSize = 'w-8 h-8',
  itemTextSize = 'text-sm',
  itemPadding = 'p-3',
  itemGap = 'gap-3',
}: SearchResultsPortalProps) => {
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!visible) return;

    const updateCoords = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 8, // separación equivalente al mt-2/mt-3 original
        left: rect.left,
        width: rect.width,
      });
    };

    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [visible, anchorRef]);

  if (!visible || !coords) return null;

  return createPortal(
    <div
      data-search-dropdown="true"
      className="fixed bg-white dark:bg-black rounded-xl shadow-xl max-h-72 overflow-y-auto dark:border dark:border-white/10"
      style={{
        top: coords.top,
        left: coords.left,
        width: coords.width,
        zIndex: 9999,
      }}
    >
      {filtered.map((empresa) => (
        <div
          key={empresa.id_empresa}
          className={`flex items-center ${itemGap} ${itemPadding} cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10`}
          onClick={() => onSelect(empresa)}
        >
          {empresa.logo ? (
            <img
              src={empresa.logo}
              alt={empresa.empresa}
              className={`${itemImgSize} rounded-full object-cover border dark:border-white/10`}
            />
          ) : (
            <div className={`${itemImgSize} flex items-center justify-center rounded-full bg-gray-200 dark:bg-white/10`}>
              <Building className="w-4 h-4 text-gray-500 dark:text-gray-300" />
            </div>
          )}
          <span className={`text-gray-700 dark:text-white font-medium ${itemTextSize}`}>
            {empresa.empresa}
          </span>
        </div>
      ))}
    </div>,
    document.body
  );
};

/**
 * Microcopy de confianza que acompaña a los CTA de registro: flecha dibujada
 * a mano apuntando al botón + "No se necesita tarjeta de crédito".
 *
 * `compact` para el header (menos alto), `light` para fondos oscuros (hero y
 * header, donde el teal no contrasta lo suficiente).
 *
 * La tipografía manuscrita (Caveat) se aplica inline porque es la única
 * familia fuera del sistema de tipos de la app y no está en el tema de
 * Tailwind. Requiere el <link> de Google Fonts en index.html.
 */
type NoCardNoteProps = {
  className?: string;
  compact?: boolean;
  light?: boolean;
};

const NoCardNote = ({ className = '', compact = false, light = false }: NoCardNoteProps) => (
  <div className={`flex items-start gap-1.5 ${compact ? 'mt-1' : 'mt-3'} ${className}`}>
    <svg
      viewBox="0 0 40 30"
      className={`${compact ? 'w-9 h-7' : 'w-8 h-6'} shrink-0 -mt-1 ${
        light ? 'text-white/70' : 'text-[#288E7F]'
      }`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 28C4 17 9 7 23 4" />
      <path d="M16 3.5 23 3l1.5 7" />
    </svg>
    <span
      className={`${compact ? 'text-[18px] pt-0.5' : 'text-[15px] pt-1'} leading-tight ${
        light ? 'text-white/85' : 'text-[#288E7F] dark:text-[#34a894]'
      }`}
      style={{ fontFamily: "'Caveat', 'Segoe Script', cursive" }}
    >
      No se necesita tarjeta de crédito
    </span>
  </div>
);

// Aparición al hacer scroll: cada bloque entra con un fundido y una subida
// corta la primera vez que asoma. `once` evita que se repita al volver arriba.
const revelar = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: 'easeOut' },
} as const;

/**
 * Icono de la fila de categorías. El match es por palabra clave sobre el
 * slug para no depender de un catálogo cerrado: cualquier categoría nueva
 * cae en el icono genérico.
 */
const iconoCategoria = (slug: string) => {
  const clases = 'w-6 h-6';
  if (/pelu|barb|estetic|belleza/.test(slug)) return <Scissors className={clases} />;
  if (/uñas|unas|nails|depil|spa|masaje/.test(slug)) return <Sparkles className={clases} />;
  if (/gym|fitness|deport|entrena|yoga/.test(slug)) return <Dumbbell className={clases} />;
  if (/salud|clinic|fisio|dental|medic|psico/.test(slug)) return <Stethoscope className={clases} />;
  if (/veterinar|mascota|animal/.test(slug)) return <PawPrint className={clases} />;
  if (/taller|coche|auto|motor/.test(slug)) return <Car className={clases} />;
  return <Building className={clases} />;
};

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [companyChoiceOpen, setCompanyChoiceOpen] = useState(false);
  const [clientChoiceOpen, setClientChoiceOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const [verificandoPWA, setVerificandoPWA] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [nombreUsuario, setNombreUsuario] = useState<string | null>(null);

  const [promo, setPromo] = useState<PromocionActiva | null>(null);

  // Progreso del fondo del header (0-1 sobre los primeros HEADER_SCROLL_RANGE px)
  const [headerProgress, setHeaderProgress] = useState(0);
  // Progreso del buscador del header (0-1 cuando el buscador del hero ya no se ve)
  const [headerSearchProgress, setHeaderSearchProgress] = useState(0);

  // Refs de los inputs de búsqueda, usados como ancla para posicionar los
  // desplegables de resultados portaleados a document.body.
  const headerSearchInputRef = useRef<HTMLDivElement>(null);
  const heroSearchInputRef = useRef<HTMLDivElement>(null);
  const mobileHeaderSearchRef = useRef<HTMLDivElement>(null);
  // Alto del header en ref: lo lee el handler de scroll sin re-suscribirse.
  const headerHRef = useRef(0);

  // Qué buscador está activo (el que el usuario ha enfocado/está usando).
  // Antes usábamos un booleano compartido, pero al compartir searchTerm
  // ambos desplegables cumplían la condición a la vez y se mostraban los
  // dos simultáneamente (p. ej. al escribir en el hero y luego hacer scroll,
  // el del header también aparecía). Con este estado solo se muestra el
  // desplegable del buscador que el usuario está usando realmente.
  const [activeSearch, setActiveSearch] = useState<'header' | 'headerMobile' | 'hero' | null>(null);

  const {
    searchTerm,
    setSearchTerm,
    filtered,
    empresas,
    userLocation,
    showLocationDialog,
    solicitarUbicacion,
    handleAllowLocation,
    handleRejectLocation,
  } = useBuscarEmpresas();

  const { getPerfilesUsuario } = useProfiles();

  // Filtros del carrusel de empresas. La categoría se elige desde la fila
  // de iconos; el resto, desde el panel de FiltrosEmpresas.
  const [filtros, setFiltros] = useState<FiltrosEmpresasValor>(FILTROS_VACIOS);
  const categoriaSel = filtros.categoria;

  // La cercanía puede pulsarse sin ubicación concedida: se abre el diálogo y
  // el filtro queda pendiente hasta que llegan las coordenadas.
  const [esperandoUbicacion, setEsperandoUbicacion] = useState(false);

  useEffect(() => {
    if (esperandoUbicacion && userLocation) {
      setFiltros((f) => ({ ...f, radioKm: RADIOS_KM[1] }));
      setEsperandoUbicacion(false);
    }
  }, [esperandoUbicacion, userLocation]);

  const pedirUbicacion = () => {
    setEsperandoUbicacion(true);
    solicitarUbicacion();
  };

  // Categorías presentes en las empresas cargadas, sin repetir
  const categorias = useMemo(() => {
    const mapa = new Map<string, string>();
    (empresas ?? []).forEach((e: any) => {
      if (e?.categoria_slug && !mapa.has(e.categoria_slug)) {
        const nombre =
          e.categoria ??
          e.categoria_slug.replace(/-/g, ' ').replace(/^./, (c: string) => c.toUpperCase());
        mapa.set(e.categoria_slug, nombre);
      }
    });
    return Array.from(mapa, ([slug, nombre]) => ({ slug, nombre })).slice(0, 12);
  }, [empresas]);

  const subcategorias = useMemo(
    () => subcategoriasDe(empresas ?? [], categoriaSel),
    [empresas, categoriaSel]
  );

  const empresasFiltradas = useMemo(() => {
    const lista = aplicarFiltros(empresas ?? [], filtros);
    if (!filtros.soloValoradas) return lista;

    // Media primero y número de valoraciones como desempate: un único 5 no
    // debe adelantar a un 4,7 con ochenta reseñas.
    return [...lista].sort((a: any, b: any) => {
      const media =
        Number(b.valoracion_media_empresa ?? 0) - Number(a.valoracion_media_empresa ?? 0);
      if (media !== 0) return media;
      return Number(b.total_valoraciones ?? 0) - Number(a.total_valoraciones ?? 0);
    });
  }, [empresas, filtros]);

  const elegirCategoria = (slug: string) =>
    setFiltros((f) => ({
      ...f,
      categoria: f.categoria === slug ? null : slug,
      subcategoria: null,
    }));

  // Buscador del header (solo PC). Va aparte del fondo: el fondo tiene que
  // ser opaco casi al instante, y el buscador solo cuando ya no está el otro.
  const headerSearchOpacity = headerSearchProgress;

  const handleSelectEmpresa = (empresa: any) => {
    setActiveSearch(null);
    navigate(`/${empresa.categoria_slug}/${empresa.ciudad_slug}/${empresa.empresa_slug}`);
  };

  // Cierra los desplegables de resultados al hacer click fuera de los
  // inputs de búsqueda y fuera del propio desplegable (que vive en un
  // portal aparte, por eso se identifica con [data-search-dropdown]).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideHeaderInput = headerSearchInputRef.current?.contains(target);
      const insideHeroInput = heroSearchInputRef.current?.contains(target);
      const insideMobileInput = mobileHeaderSearchRef.current?.contains(target);
      const insideDropdown = (target as HTMLElement).closest?.('[data-search-dropdown]');
      if (!insideHeaderInput && !insideHeroInput && !insideMobileInput && !insideDropdown) {
        setActiveSearch(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Header: transición de color al hacer scroll
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        setHeaderProgress(Math.min(scrollTop / HEADER_SCROLL_RANGE, 1));

        // El buscador del header solo aparece cuando el del hero ya está
        // tapado por el header: se mide su posición real, no una estimación,
        // para que nunca se vean los dos a la vez.
        const rect = heroSearchInputRef.current?.getBoundingClientRect();
        const oculto = rect ? headerHRef.current - rect.bottom : 0;
        setHeaderSearchProgress(Math.max(0, Math.min(1, oculto / HEADER_SEARCH_FADE_PX)));
        ticking = false;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Carga la promoción activa (para el banner de la sección Empresa)
  useEffect(() => {
    cargarPromocionActiva().then(setPromo);
  }, []);

  // Alto real del header fijo. El hero ya no lleva spacer, así que sin esto
  // su contenido quedaría por debajo del logo y los botones. Se mide en vez
  // de fijarlo a ojo porque en móvil el header tiene una fila más.
  //
  // El nodo se guarda en estado, no en un ref: mientras `verificandoPWA` está
  // activo se pinta otra pantalla y el header todavía no existe, así que un
  // efecto con [] lo encontraría vacío y no volvería a mirar nunca.
  const [headerEl, setHeaderEl] = useState<HTMLDivElement | null>(null);
  const [headerH, setHeaderH] = useState(0);

  useEffect(() => {
    if (!headerEl) return;
    const medir = () => {
      headerHRef.current = headerEl.offsetHeight;
      setHeaderH(headerEl.offsetHeight);
    };
    medir();
    const observer = new ResizeObserver(medir);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [headerEl]);

  // ── Parallax interno del hero ──
  // Los elementos decorativos se mueven a distinta velocidad que la página
  // para dar profundidad mientras el hero se va.
  const { scrollY } = useScroll();

  // Los orbs se desplazan muy lentamente.
  const orbsY = useTransform(scrollY, [0, PARALLAX_SCROLL_RANGE], [0, 55]);
  // Las tarjetas flotantes se desplazan algo más rápido que los orbs.
  const cardsY = useTransform(scrollY, [0, PARALLAX_SCROLL_RANGE], [0, 130]);
  // El buscador tiene su propio desplazamiento independiente y sutil.
  const searchY = useTransform(scrollY, [0, PARALLAX_SCROLL_RANGE], [0, -25]);
  // El contenido del hero se apaga suavemente mientras sale de pantalla.
  const heroContentScale = useTransform(scrollY, [0, PARALLAX_SCROLL_RANGE], [1, 0.94]);
  const heroContentOpacity = useTransform(scrollY, [0, PARALLAX_SCROLL_RANGE], [1, 0.35]);

  // Nota: esta pantalla mantiene su propia gestión de tema (hero siempre oscuro
  // por diseño), por eso NO forzamos remove('dark') como antes; el modo oscuro
  // del resto de la app se respeta a través de las clases dark: añadidas abajo.
  useEffect(() => {
    const espera = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      setLoadProgress(15);
      await espera(250);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        setLoadProgress(100);
        await espera(400);
        setVerificandoPWA(false);
        return;
      }

      setLoadProgress(45);
      setNombreUsuario(
        session.user.user_metadata?.full_name || session.user.email || null
      );
      await espera(250);

      const perfiles = await getPerfilesUsuario(session.user.id);
      setLoadProgress(75);
      await espera(250);

      if (!perfiles || perfiles.length === 0) {
        setLoadProgress(100);
        await espera(400);
        navigate('/profiles', { replace: true });
        return;
      }

      const perfilAuto = perfiles.find((p) => p.inicio_automatico);
      let destino: { id_perfil: string; id_empresa: string; ruta_panel: string } | null = null;

      if (perfilAuto) {
        destino = perfilAuto as any;
      } else if (perfiles.length === 1) {
        destino = perfiles[0] as any;
      }

      setLoadProgress(100);
      // Espera a que la barra termine de animarse (misma duración que la
      // transition de abajo) antes de desmontar con el navigate.
      await espera(400);

      if (destino) {
        localStorage.setItem('id_perfil', destino.id_perfil || '');
        localStorage.setItem('id_empresa', destino.id_empresa || '');
        navigate(rutaInicioPanel(destino.ruta_panel), { replace: true });
        return;
      }

      navigate('/profiles', { replace: true });
    };
    run();
  }, [navigate, getPerfilesUsuario]);

  const tools = [
    { icon: <Building className="w-4 h-4" />, key: 'centralized' },
    { icon: <BarChart3 className="w-4 h-4" />, key: 'analytics' },
    { icon: <Calendar className="w-4 h-4" />, key: 'calendar' },
    { icon: <Users className="w-4 h-4" />, key: 'team' },
    { icon: <HeartHandshake className="w-4 h-4" />, key: 'clients' },
    { icon: <TrendingUp className="w-4 h-4" />, key: 'growth' },
  ];

  if (verificandoPWA) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-6 px-6 overflow-hidden">
        {/* Fondo — mismo lenguaje visual que el hero */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background:
              'linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)',
          }}
        />
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background:
              'linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)',
          }}
        />

        <motion.img
          src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
          alt="Bookoll"
          className="relative z-10 h-12"
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        />

        <motion.div
          className="relative z-10 text-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <h1 className="text-xl sm:text-2xl font-semibold text-white">
            {nombreUsuario ? `Hola de nuevo, ${nombreUsuario}` : 'Bienvenido de nuevo'}
          </h1>
          <p className="mt-1.5 text-sm text-white/60">
            Estamos preparando tu panel…
          </p>
        </motion.div>

        <div className="relative z-10 w-full max-w-xs h-1.5 rounded-full bg-white/15 overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{
              background: 'linear-gradient(90deg, #288E7F 0%, #34a894 100%)',
            }}
            initial={{ width: '0%' }}
            animate={{ width: `${loadProgress}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
      </div>
    );
  }

  // App nativa (Capacitor): pantalla simple con buscador, sin landing web
  if (Capacitor.isNativePlatform()) {
    return <MobileLoginScreen />;
  }

  return (
    <>
      {/* ═══════════════ HEADER FIJO (fuera del motion.div para que
          position:fixed sea siempre relativo al viewport).
          En móvil es de dos filas (más alto): fila 1 logo + iniciar sesión,
          fila 2 el botón "Incluir tu negocio" centrado. ═══════════════ */}
      <div ref={setHeaderEl} className="fixed top-0 inset-x-0 z-50 px-6 py-3">
        {/* Fondo header — modo claro (azul/verde, igual que antes) */}
        <div
          className="absolute top-0 left-0 right-0 dark:hidden transition-opacity duration-150 ease-out pointer-events-none lg:!bottom-0"
          style={{
            background:
              'linear-gradient(120deg, rgba(16,50,84,0.98) 0%, rgba(10,36,32,0.98) 100%)',
            opacity: headerProgress,
            // Se estira bajo el header lo que mide la fila del buscador móvil
            // mientras esta aparece; en PC (lg) se queda en el header.
            bottom: -MOBILE_SEARCH_ROW_PX * headerSearchOpacity,
          }}
        />
        {/* Fondo header — modo oscuro (negro + resplandor teal, coherente con el hero) */}
        <div
          className="absolute top-0 left-0 right-0 hidden dark:block transition-opacity duration-150 ease-out pointer-events-none lg:!bottom-0"
          style={{
            background:
              'radial-gradient(ellipse at 20% 50%, rgba(40,142,127,0.15) 0%, transparent 60%), linear-gradient(180deg, rgba(5,8,7,0.98) 0%, rgba(0,0,0,0.98) 100%)',
            opacity: headerProgress,
            bottom: -MOBILE_SEARCH_ROW_PX * headerSearchOpacity,
          }}
        />

        {/* ── Fila 0 (solo móvil): franja a sangre para el acceso de negocio ── */}
        {/* El <button> no se estira aunque sea flex (ancho intrínseco), así
            que la sangre y el ancho completo los da este contenedor */}
        <div className="sm:hidden relative z-10 -mx-6 -mt-3 mb-3 border-b border-white/15">
          <button
            type="button"
            onClick={() => setCompanyChoiceOpen(true)}
            className="flex h-11 w-full items-center justify-center gap-2 text-sm font-medium text-white"
          >
            {t('login.includeBusiness')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* ── Fila 1: logo + buscador (solo PC, aparece al hacer scroll) + iniciar sesión ── */}
        <div className="relative z-10 flex items-center justify-between gap-3">
          <motion.img
            src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
            alt="Bookoll — Software de reservas online"
            className="h-8 sm:h-10 sm:ml-[10%] shrink-0"
            fetchPriority="high"
            crossOrigin="anonymous"
            decoding="async"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
          />

          {/* Buscador del header: solo desktop, ocupa el espacio central entre
              el logo y los botones y aparece con fade+slide al hacer scroll. */}
          <div className="hidden lg:flex flex-1 justify-center px-6">
            <div
              className="w-full max-w-2xl"
              style={{
                opacity: headerSearchOpacity,
                transform: `translateY(${(1 - headerSearchOpacity) * -8}px)`,
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                pointerEvents: headerSearchOpacity > 0.05 ? 'auto' : 'none',
              }}
            >
              <div className="relative" ref={headerSearchInputRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#288E7F] w-4 h-4" />
                <Input
                  type="text"
                  placeholder={t('login.searchPlaceholder')}
                  ref={(el) => {
                    if (el) {
                      el.style.setProperty('height', '38px', 'important');
                      el.style.setProperty('padding-top', '0px', 'important');
                      el.style.setProperty('padding-bottom', '0px', 'important');
                      el.style.setProperty('box-shadow', '0 1px 3px rgba(0,0,0,0.15)', 'important');
                    }
                  }}
                  className="w-full rounded-lg pl-9 pr-3 text-sm text-gray-700 focus:ring-2 focus:ring-[#103254] bg-white/95 dark:bg-black/95 dark:text-white dark:placeholder:text-gray-500 dark:focus:ring-white/30 dark:border dark:border-[#288E7F]/60"
                  value={searchTerm}
                  onFocus={() => setActiveSearch('header')}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setActiveSearch('header');
                  }}
                />
                <SearchResultsPortal
                  anchorRef={headerSearchInputRef}
                  visible={activeSearch === 'header' && headerSearchOpacity > 0.05 && !!searchTerm && filtered.length > 0}
                  filtered={filtered}
                  onSelect={handleSelectEmpresa}
                  itemImgSize="w-8 h-8"
                  itemTextSize="text-sm"
                  itemPadding="p-3"
                  itemGap="gap-3"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 sm:mr-[10%] shrink-0">
            {/* Dos CTA sólidos (negocio y cliente) e "Iniciar sesión"
                como texto al final del grupo */}
            {/* Wrapper `relative` + nota en `absolute`: el microcopy cuelga
                bajo el enlace sin aumentar la altura del header */}
            <div className="relative hidden sm:block">
              <Button
                size="sm"
                className="rounded-md px-3 sm:px-4 font-semibold text-white border-0 shadow hover:brightness-110 whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #288E7F 0%, #1e7a6e 100%)' }}
                onClick={() => setCompanyChoiceOpen(true)}
              >
                {t('login.includeBusiness')}
              </Button>
              {/* La nota cuelga fuera del fondo del header, así que al hacer
                  scroll quedaría flotando sobre el contenido de la página:
                  se desvanece al mismo ritmo que el header se vuelve sólido */}
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 ml-8 whitespace-nowrap pointer-events-none"
                style={{
                  opacity: Math.max(0, 1 - headerProgress * 3),
                  transition: 'opacity 0.25s ease',
                }}
              >
                <NoCardNote compact light />
              </div>
            </div>

            <Button
              size="sm"
              className="h-8 rounded-md px-2.5 text-xs sm:h-9 sm:px-4 sm:text-sm font-semibold text-white border-0 shadow hover:brightness-110 whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #288E7F 0%, #1e7a6e 100%)' }}
              onClick={() => setClientChoiceOpen(true)}
            >
              <span className="sm:hidden">{t('login.registerClientShort', 'Crear cuenta')}</span>
              <span className="hidden sm:inline">
                {t('login.registerAsClient', 'Regístrate como cliente')}
              </span>
            </Button>

            <LoginDialog
              open={loginOpen}
              setOpen={setLoginOpen}
              context="login"
              onRegisterClient={() =>
                setTimeout(() => setClientChoiceOpen(true), 220)
              }
            >
              <Button
                size="sm"
                className="h-8 rounded-md px-2 text-xs sm:h-9 sm:px-3 sm:text-sm font-medium shadow-none bg-transparent text-white/85 hover:text-white hover:bg-white/10 whitespace-nowrap"
              >
                {t('login.signIn')}
              </Button>
            </LoginDialog>
          </div>
        </div>

        {/* ── Buscador del header en móvil: cuelga bajo la barra cuando el del
            hero ya no se ve. Va en absoluto y no dentro del flujo para que
            el alto del header (que el hero usa de padding) no cambie. ── */}
        <div
          className="lg:hidden absolute top-full inset-x-0 h-14 px-4 pb-3 pt-1"
          style={{
            opacity: headerSearchOpacity,
            transform: `translateY(${(1 - headerSearchOpacity) * -6}px)`,
            transition: 'opacity 0.25s ease, transform 0.25s ease',
            pointerEvents: headerSearchOpacity > 0.05 ? 'auto' : 'none',
          }}
        >
          <div className="relative" ref={mobileHeaderSearchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#288E7F] w-4 h-4" />
            <Input
              type="text"
              placeholder={t('login.searchPlaceholder')}
              className="h-10 w-full rounded-lg pl-9 pr-3 text-sm text-gray-700 bg-white/95 dark:bg-black/95 dark:text-white dark:placeholder:text-gray-500 dark:border dark:border-[#288E7F]/60 focus:ring-2 focus:ring-[#288E7F]"
              value={searchTerm}
              onFocus={() => setActiveSearch('headerMobile')}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setActiveSearch('headerMobile');
              }}
            />
            <SearchResultsPortal
              anchorRef={mobileHeaderSearchRef}
              visible={activeSearch === 'headerMobile' && headerSearchOpacity > 0.05 && !!searchTerm && filtered.length > 0}
              filtered={filtered}
              onSelect={handleSelectEmpresa}
              itemImgSize="w-8 h-8"
              itemTextSize="text-sm"
              itemPadding="p-3"
              itemGap="gap-3"
            />
          </div>
        </div>

      </div>

      <motion.div
        className="relative flex flex-col min-h-screen"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        {/* ═══════════════ SEO ═══════════════ */}
        <HomeSEO />

        {/* ═══════════════ HERO ═══════════════
            Va en el flujo normal, no fijo: sube con la página y el bloque de
            abajo no lo invade. El corte queda dentro del propio degradado del
            hero, que ya termina en blanco (negro en modo oscuro). */}
        <div
          className="relative z-0 flex flex-col items-center justify-center text-center px-6 pt-[108px] sm:pt-[76px] overflow-hidden"
          style={{
            height: `calc(${HERO_HEIGHT} + ${headerH}px)`,
            // Las clases pt-* de arriba son el valor por defecto, calculado a
            // mano para las dos alturas del header. La medida real solo pisa
            // ese valor cuando existe, así el título nunca queda debajo de los
            // botones aunque la medición falle.
            ...(headerH ? { paddingTop: headerH + 12 } : {}),
          }}
        >
          {/* Fondos — fijos, sin parallax (evita huecos al desplazarse) */}
          <div className="absolute inset-0">
            {/* Fondo modo claro */}
            <div
              className="absolute inset-0 dark:hidden"
              style={{
                background: `
                  radial-gradient(ellipse at 15% 60%, rgba(40, 142, 127, 0.22) 0%, transparent 50%),
                  radial-gradient(ellipse at 85% 20%, rgba(16, 50, 84, 0.5) 0%, transparent 55%),
                  linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
                `,
              }}
            />
            {/* Fondo modo oscuro: negro con resplandor teal, funde con el resto de la app */}
            <div
              className="absolute inset-0 hidden dark:block"
              style={{
                background: `
                  radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
                  radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
                  linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
                `,
              }}
            />
          </div>

          {/* Estrellas / brillos — sutil parpadeo. */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `
                radial-gradient(1.5px 1.5px at 12% 18%, rgba(255,255,255,0.9) 0%, transparent 100%),
                radial-gradient(1px 1px at 28% 42%, rgba(255,255,255,0.7) 0%, transparent 100%),
                radial-gradient(2px 2px at 45% 12%, rgba(255,255,255,0.85) 0%, transparent 100%),
                radial-gradient(1px 1px at 63% 30%, rgba(255,255,255,0.65) 0%, transparent 100%),
                radial-gradient(1.5px 1.5px at 78% 20%, rgba(255,255,255,0.8) 0%, transparent 100%),
                radial-gradient(1px 1px at 88% 48%, rgba(255,255,255,0.6) 0%, transparent 100%),
                radial-gradient(1.5px 1.5px at 20% 68%, rgba(40,142,127,0.8) 0%, transparent 100%),
                radial-gradient(1px 1px at 52% 60%, rgba(255,255,255,0.7) 0%, transparent 100%),
                radial-gradient(2px 2px at 72% 72%, rgba(255,255,255,0.85) 0%, transparent 100%),
                radial-gradient(1px 1px at 35% 82%, rgba(255,255,255,0.6) 0%, transparent 100%),
                radial-gradient(1.5px 1.5px at 90% 78%, rgba(40,142,127,0.75) 0%, transparent 100%),
                radial-gradient(1px 1px at 8% 50%, rgba(255,255,255,0.65) 0%, transparent 100%)
              `,
              backgroundRepeat: 'no-repeat',
            }}
            animate={{ opacity: [0.55, 1, 0.55] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Orbs — parallax lento */}
          <motion.div className="absolute inset-0 pointer-events-none" style={{ y: orbsY }}>
            <motion.div
              className="absolute top-24 left-[5%] w-64 h-64 rounded-full pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle, rgba(40,142,127,0.15) 0%, transparent 70%)',
                filter: 'blur(40px)',
              }}
              animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.9, 0.5] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute top-10 right-[8%] w-72 h-72 rounded-full pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
                filter: 'blur(50px)',
              }}
              animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.8, 0.4] }}
              transition={{
                duration: 9,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 3,
              }}
            />
          </motion.div>

          {/* ─── Tarjetas flotantes glassmorphism (solo desktop, decorativas) —
              parallax algo más rápido que los orbs ─── */}
          <motion.div className="absolute inset-0 pointer-events-none" style={{ y: cardsY }}>
            <motion.div
              className="hidden lg:flex absolute top-[16%] left-[16%] w-16 h-16 rounded-[18px] items-center justify-center pointer-events-none z-[1]"
              style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.25)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                rotate: '-8deg',
              }}
              animate={{ y: [0, -16, 0], rotate: [-8, -4, -8] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Calendar className="w-6 h-6 text-white/80" />
            </motion.div>

            <motion.div
              className="hidden lg:flex absolute top-[10%] right-[20%] w-12 h-12 rounded-[16px] items-center justify-center pointer-events-none z-[1]"
              style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.25)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                rotate: '10deg',
              }}
              animate={{ y: [0, 14, 0], rotate: [10, 6, 10] }}
              transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            >
              <Smartphone className="w-5 h-5 text-white/80" />
            </motion.div>

            <motion.div
              className="hidden lg:flex absolute bottom-[14%] left-[20%] w-14 h-14 rounded-[16px] items-center justify-center pointer-events-none z-[1]"
              style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.25)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                rotate: '6deg',
              }}
              animate={{ y: [0, -12, 0], rotate: [6, 10, 6] }}
              transition={{ duration: 7.5, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
            >
              <BarChart3 className="w-6 h-6 text-white/80" />
            </motion.div>

            <motion.div
              className="hidden lg:flex absolute bottom-[18%] right-[18%] w-14 h-14 rounded-[16px] items-center justify-center pointer-events-none z-[1]"
              style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.25)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                rotate: '-6deg',
              }}
              animate={{ y: [0, 15, 0], rotate: [-6, -10, -6] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            >
              <Building className="w-6 h-6 text-white/80" />
            </motion.div>

            <motion.div
              className="hidden lg:flex absolute top-[36%] left-[12%] w-12 h-12 rounded-[16px] items-center justify-center pointer-events-none z-[1]"
              style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.25)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                rotate: '9deg',
              }}
              animate={{ y: [0, -13, 0], rotate: [9, 13, 9] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1.6 }}
            >
              <Users className="w-5 h-5 text-white/80" />
            </motion.div>
          </motion.div>

          {/* Contenido del hero (título, buscador, botón...): se achica y
              pierde opacidad ligeramente al hacer scroll, sin afectar al
              fondo, que sigue cubriendo toda la pantalla. */}
          <motion.div
            className="flex flex-col items-center relative z-10"
            style={{ scale: heroContentScale, opacity: heroContentOpacity }}
          >
          {/* Título */}
          <HeroRotatingTitle />

          <motion.div
            className="relative z-10 mt-4 sm:mt-5 h-px w-20 mx-auto"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          />

          {/* Buscador — desplazamiento independiente y sutil */}
          <motion.div className="relative z-20 w-full max-w-xl" style={{ y: searchY }}>
            <motion.div
              className="mt-7 sm:mt-10 w-full"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="relative group" ref={heroSearchInputRef}>
                <Search className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#288E7F] w-5 h-5 sm:w-6 sm:h-6" />
                <Input
                  type="text"
                  placeholder={t('login.searchPlaceholder')}
                  className="w-full rounded-lg pl-11 sm:pl-14 pr-4 sm:pr-5 py-4 sm:py-5 text-base sm:text-lg text-gray-700 shadow-2xl focus:ring-2 focus:ring-[#103254] bg-white/95 dark:bg-black/95 dark:text-white dark:placeholder:text-gray-500 dark:focus:ring-white/30 dark:border dark:border-[#288E7F]/60"
                  value={searchTerm}
                  onFocus={() => setActiveSearch('hero')}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setActiveSearch('hero');
                  }}
                />
                <div
                  className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-3/4 h-6 rounded-full pointer-events-none"
                  style={{
                    background: 'rgba(16,50,84,0.15)',
                    filter: 'blur(10px)',
                  }}
                />
              </div>
              <SearchResultsPortal
                anchorRef={heroSearchInputRef}
                visible={activeSearch === 'hero' && filtered.length > 0}
                filtered={filtered}
                onSelect={handleSelectEmpresa}
                itemImgSize="w-10 h-10"
                itemTextSize="text-base"
                itemPadding="p-4"
                itemGap="gap-4"
              />
            </motion.div>
          </motion.div>

          <motion.div
            className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm text-white/70 text-center relative z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            {/* Android — Google Play */}
            <a
              href={URL_GOOGLE_PLAY}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 min-w-[220px] text-white/90 hover:text-white hover:brightness-110 transition"
              style={{
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.25)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              }}
            >
              <svg className="w-5 h-5 shrink-0" viewBox="0 0 40 40" aria-hidden="true">
                <path fill="#EA4335" d="M19.7,19.2L4.3,35.3c0,0,0,0,0,0c0.5,1.7,2.1,3,4,3c0.8,0,1.5-0.2,2.1-0.6l0,0l17.4-9.9L19.7,19.2z" />
                <path fill="#FBBC04" d="M35.3,16.4L35.3,16.4l-7.5-4.3l-8.4,7.4l8.5,8.3l7.5-4.2c1.3-0.7,2.2-2.1,2.2-3.6C37.5,18.5,36.6,17.1,35.3,16.4z" />
                <path fill="#4285F4" d="M4.3,4.7C4.2,5,4.2,5.4,4.2,5.8v28.5c0,0.4,0,0.7,0.1,1.1l16-15.7L4.3,4.7z" />
                <path fill="#34A853" d="M19.8,20l8-7.9L10.5,2.3C9.9,1.9,9.1,1.7,8.3,1.7c-1.9,0-3.6,1.3-4,3c0,0,0,0,0,0L19.8,20z" />
              </svg>
              {t('login.mobileAndroid')}
            </a>

            {/* iOS — próximamente */}
            <span
              className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 min-w-[220px] text-white/55 cursor-default select-none"
              style={{
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <svg className="w-5 h-5 shrink-0 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M16.365 1.43c0 1.14-.417 2.2-1.1 2.98-.74.85-1.94 1.5-3.06 1.42-.13-1.1.44-2.27 1.08-3 .72-.83 1.98-1.44 3.08-1.4zM20.5 17.02c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.39 3.52-4.12 3.53-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.78-4.04-3.35-2.77-4.4-3.06-9.56-1.35-12.3 1.21-1.94 3.13-3.08 4.93-3.08 1.83 0 2.98 1 4.5 1 1.47 0 2.37-1 4.49-1 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.32 7.82.24 9.85z" />
              </svg>
              {t('login.mobileIos')}
            </span>
          </motion.div>
          </motion.div>
        </div>

        {/* ═══════════════ BLOQUE INFERIOR ═══════════════ */}
        <div className="relative z-10 flex flex-col bg-white dark:bg-black text-gray-800 dark:text-gray-200">

          {/* Transición suave hero → bloque: glow que se desvanece, para que
              el corte de color no entre tan brusco. */}
          <div
            className="hidden dark:block absolute top-0 inset-x-0 h-64 sm:h-80 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(40,142,127,0.16) 0%, rgba(16,50,84,0.08) 40%, transparent 100%)',
            }}
          />

          {/* ── Categorías: el filtro va pegado al carrusel que gobierna, para
              que el efecto de pulsar se vea sin desplazarse ── */}
          {categorias.length > 0 && (
            <div className="w-full bg-white dark:bg-black pt-8 sm:pt-10">
              <div className="w-full sm:px-[15%]">
                <div className="overflow-x-auto px-6 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <div className="flex w-max gap-5 sm:gap-7">
                    {categorias.map((c) => {
                      const activa = categoriaSel === c.slug;
                      return (
                        <button
                          key={c.slug}
                          type="button"
                          onClick={() => elegirCategoria(c.slug)}
                          className="flex w-16 sm:w-20 flex-col items-center gap-2"
                          aria-pressed={activa}
                        >
                          <span
                            className={`flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full border transition-colors ${
                              activa
                                ? 'border-transparent bg-[#288E7F] text-white'
                                : 'border-gray-200 bg-gray-50 text-[#103254] dark:border-white/15 dark:bg-white/10 dark:text-white'
                            }`}
                          >
                            {iconoCategoria(c.slug)}
                          </span>
                          <span
                            className={`text-[11px] sm:text-xs leading-tight text-center ${
                              activa
                                ? 'font-semibold text-[#288E7F]'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {c.nombre}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <FiltrosEmpresas
                  className="mt-5 px-6 sm:px-0"
                  valor={filtros}
                  onChange={setFiltros}
                  subcategorias={subcategorias}
                  tieneUbicacion={!!userLocation}
                  onPedirUbicacion={pedirUbicacion}
                />

              </div>
            </div>
          )}

          {/* Sin resultados: solo cuando hay empresas cargadas y son los
              filtros los que las dejan fuera, no mientras carga la lista. */}
          {empresas.length > 0 && empresasFiltradas.length === 0 ? (
            <div className="w-full px-6 sm:px-[15%] py-12 flex flex-col items-center text-center">
              <SearchX className="w-8 h-8 text-gray-300 dark:text-white/25" />
              <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('login.filters.noResultsTitle')}
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('login.filters.noResultsHint')}
              </p>
              <button
                type="button"
                onClick={() => setFiltros(FILTROS_VACIOS)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-50 dark:border-white/15 dark:text-gray-300 dark:hover:bg-white/10"
              >
                <X className="w-3.5 h-3.5" />
                {t('login.filters.clear')}
              </button>
            </div>
          ) : (
            <RecommendedCompanies empresas={empresasFiltradas} />
          )}
          <div className="w-full h-px bg-gray-100 dark:bg-white/10" />
          {/* ── Sección Empresa ── */}
          <section className="relative overflow-hidden w-full bg-white dark:bg-black py-16 px-4 sm:px-0">
            {/* Ambiente de sección: mismo lenguaje de color que el hero y la
                promoción, a baja opacidad, para que todo se sienta conectado
                en vez de negro plano con una caja de color aislada. */}
            <div
              className="hidden dark:block absolute inset-0 pointer-events-none"
              style={{
                background: `
                  radial-gradient(ellipse at 10% 0%, rgba(40,142,127,0.10) 0%, transparent 45%),
                  radial-gradient(ellipse at 90% 100%, rgba(16,50,84,0.14) 0%, transparent 45%)
                `,
              }}
            />
            <motion.div {...revelar} className="relative z-10 w-full sm:px-[15%]">
              <div className="flex flex-col md:flex-row justify-between gap-12 items-center px-6 sm:px-0">
                <div>
                  <h2 className="text-4xl sm:text-5xl font-semibold mb-5 text-[#103254] dark:text-white">
                    {t('login.businessTitle')}
                  </h2>
                  <p className="text-lg text-gray-500 dark:text-gray-400 mb-7 max-w-xl leading-relaxed">
                    {t('login.businessText')}
                  </p>
                  <Button
                    className="rounded-lg px-6 mx-auto lg:mx-0 block dark:hidden bg-[#103254] hover:bg-[#0d2742]"
                    onClick={() => setCompanyChoiceOpen(true)}
                  >
                    {t('login.includeBusiness')}
                  </Button>
                  <Button
                    className="hidden dark:block rounded-lg px-6 mx-auto lg:mx-0 border-0 text-white hover:brightness-110"
                    style={{ background: 'linear-gradient(135deg, #288E7F 0%, #1e7a6e 100%)' }}
                    onClick={() => setCompanyChoiceOpen(true)}
                  >
                    {t('login.includeBusiness')}
                  </Button>
                  {/* Una sola nota para los dos botones: son la variante
                      clara y oscura del mismo CTA, nunca visibles a la vez */}
                  <NoCardNote className="justify-center lg:justify-start" />
                </div>
                <div>
                  <img
                    src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/imagenes%20bookoll/perfil_empresa_img_portada.png"
                    className="block w-[220px] h-[200px] sm:w-[320px] sm:h-[280px] lg:w-[520px] lg:h-[460px] object-contain mx-auto rounded-xl"
                    alt="Gestión en tu smartphone"
                  />
                </div>
              </div>
            </motion.div>

            {/* Banner promoción — texto siempre acorde a la promo activa
                en la BD (vista_promocion_registro_activa). Si no hay
                ninguna activa, el banner no se muestra. */}
            {promo && (
              <div className="relative mt-14 py-12 px-6 -mx-4 sm:-mx-0 overflow-hidden">
                {/* Fondo modo claro: gradiente verde */}
                <div
                  className="absolute inset-0 dark:hidden"
                  style={{
                    background:
                      'linear-gradient(135deg, #2fa88f 0%, #22916f 50%, #1a7d55 100%)',
                  }}
                />
                {/* Fondo modo oscuro: mismo fondo que el hero (negro + resplandor teal) */}
                <div
                  className="absolute inset-0 hidden dark:block"
                  style={{
                    background: `
                      radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
                      radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
                      linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
                    `,
                  }}
                />
                <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
                  <p className="text-2xl sm:text-3xl font-semibold">
                    Conviértete en Negocio Fundador
                  </p>
                  <p className="mt-2 text-lg sm:text-xl font-normal text-white/90 leading-relaxed">
                    Sé uno de los primeros negocios en unirte y consigue tu
                    suscripción gratuita de por vida.
                  </p>
                  <p className="mt-3 text-xs sm:text-sm font-semibold uppercase tracking-wide text-white/80">
                    Solo para los primeros negocios. Plazas limitadas.
                  </p>
                </div>
              </div>
            )}
          </section>
          <div className="w-full h-px bg-gray-100 dark:bg-white/10" />
          {/* ── Sección Trabajador ── */}
          <section className="relative overflow-hidden w-full bg-gray-50 dark:bg-[#1c1c1e] py-16 px-4 sm:px-0">
            {/* Ambiente de sección (tono teal) para diferenciarla de las de
                alrededor, en vez de un gris plano sin vida. */}
            <div
              className="hidden dark:block absolute inset-0 pointer-events-none"
              style={{
                background: `
                  radial-gradient(ellipse at 85% 0%, rgba(40,142,127,0.12) 0%, transparent 50%),
                  radial-gradient(ellipse at 5% 100%, rgba(40,142,127,0.07) 0%, transparent 45%)
                `,
              }}
            />
            <motion.div {...revelar} className="relative z-10 w-full sm:px-[15%]">
              <div className="flex flex-col md:flex-row justify-between gap-12 items-center px-6 sm:px-0">
                <div>
                  <img
                    src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/imagenes%20bookoll/perfil_trabajador_img_portada.png"
                    className="block w-[220px] h-[200px] sm:w-[320px] sm:h-[280px] lg:w-[520px] lg:h-[460px] object-contain mx-auto rounded-xl"
                    alt="Agenda diaria del trabajador"
                  />
                </div>
                <div>
                  <h2 className="text-4xl sm:text-5xl font-semibold mb-5 text-[#103254] dark:text-white">
                    {t('login.workerTitle')}
                  </h2>
                  <p className="text-lg text-gray-500 dark:text-gray-400 mb-7 max-w-xl leading-relaxed">
                    {t('login.workerText')}
                  </p>
                </div>
              </div>
            </motion.div>
          </section>
          <div className="w-full h-px bg-gray-100 dark:bg-white/10" />
          {/* ── Sección Cliente ── */}
          <section className="relative overflow-hidden w-full bg-white dark:bg-black py-16 px-4 sm:px-0">
            <div
              className="hidden dark:block absolute inset-0 pointer-events-none"
              style={{
                background: `
                  radial-gradient(ellipse at 15% 0%, rgba(16,50,84,0.16) 0%, transparent 50%),
                  radial-gradient(ellipse at 90% 100%, rgba(40,142,127,0.08) 0%, transparent 45%)
                `,
              }}
            />
            <motion.div {...revelar} className="relative z-10 w-full sm:px-[15%]">
              <div className="flex flex-col md:flex-row justify-between gap-12 items-center px-6 sm:px-0">
                <div>
                  <h2 className="text-4xl sm:text-5xl font-semibold mb-5 text-[#103254] dark:text-white">
                    {t('login.clientTitle')}
                  </h2>
                  <p className="text-lg text-gray-500 dark:text-gray-400 mb-7 max-w-xl leading-relaxed">
                    {t('login.clientText')}
                  </p>
                  <Button
                    className="rounded-lg px-6 mx-auto lg:mx-0 block dark:hidden bg-[#103254] hover:bg-[#0d2742]"
                    onClick={() => setClientChoiceOpen(true)}
                  >
                    {t('login.createClient')}
                  </Button>
                  <Button
                    className="hidden dark:block rounded-lg px-6 mx-auto lg:mx-0 border-0 text-white hover:brightness-110"
                    style={{ background: 'linear-gradient(135deg, #288E7F 0%, #1e7a6e 100%)' }}
                    onClick={() => setClientChoiceOpen(true)}
                  >
                    {t('login.createClient')}
                  </Button>
                </div>
                <div>
                  <img
                    src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/imagenes%20bookoll/perfil_cliente_img_portada.png"
                    className="block w-[220px] h-[200px] sm:w-[320px] sm:h-[280px] lg:w-[520px] lg:h-[460px] object-contain mx-auto rounded-xl"
                    alt="Cliente reservando desde el móvil"
                  />
                </div>
              </div>
            </motion.div>
          </section>
          <div className="w-full h-px bg-gray-100 dark:bg-white/10" />
          {/* ── Herramientas ── */}
          <section className="relative overflow-hidden w-full bg-gray-50 dark:bg-[#1c1c1e] py-20 px-4 sm:px-0">
            <div
              className="hidden dark:block absolute inset-0 pointer-events-none"
              style={{
                background: `
                  radial-gradient(ellipse at 10% 100%, rgba(40,142,127,0.10) 0%, transparent 45%),
                  radial-gradient(ellipse at 95% 0%, rgba(16,50,84,0.12) 0%, transparent 45%)
                `,
              }}
            />
            <motion.div {...revelar} className="relative z-10 w-full sm:px-[15%]">
              <h2 className="text-4xl sm:text-5xl font-semibold mb-4 text-[#103254] dark:text-white">
                {t('login.toolsTitle')}
              </h2>
              <p className="text-lg text-gray-500 dark:text-gray-400 mb-14 leading-relaxed max-w-2xl">
                {t('login.toolsSubtitle')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                {tools.map((item, i) => (
                  <motion.div
                    key={item.key}
                    className="flex flex-col gap-3"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.4, delay: i * 0.07 }}
                  >
                    <div className="flex items-center gap-3">
                      <motion.div
                        className="w-8 h-8 rounded-lg bg-[#103254]/8 dark:bg-white/10 flex items-center justify-center text-[#103254] dark:text-white"
                        animate={{ y: [0, -4, 0] }}
                        transition={{
                          duration: 3.5,
                          repeat: Infinity,
                          ease: 'easeInOut',
                          delay: i * 0.3,
                        }}
                      >
                        {item.icon}
                      </motion.div>
                      <h3 className="text-base font-semibold text-[#103254] dark:text-white">
                        {t(`login.tools.${item.key}.title`)}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed ml-[44px]">
                      {t(`login.tools.${item.key}.text`)}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>

          <div className="w-full h-px bg-gray-100 dark:bg-white/10" />

          <CategoryCityLinks empresas={empresas} />

          <div className="w-full h-px bg-gray-100 dark:bg-white/10" />

          <HomeSEOAccordion />

          <div className="w-full h-px bg-gray-100 dark:bg-white/10" />

          <Footer />
        </div>

        {/* ═══════════════ DIALOGS ═══════════════ */}
        {/* El retardo deja terminar la animación de cierre antes de abrir
            el otro diálogo; si se solapan, pelean por el foco */}
        <ClientRegisterChoiceDialog
          open={clientChoiceOpen}
          onOpenChange={setClientChoiceOpen}
          onSwitch={() => {
            setClientChoiceOpen(false);
            setTimeout(() => setCompanyChoiceOpen(true), 220);
          }}
        />
        <CompanyRegisterChoiceDialog
          open={companyChoiceOpen}
          onOpenChange={setCompanyChoiceOpen}
          onSwitch={() => {
            setCompanyChoiceOpen(false);
            setTimeout(() => setClientChoiceOpen(true), 220);
          }}
        />

        <LocationDialog
          open={showLocationDialog}
          onAllow={handleAllowLocation}
          onReject={() => {
            setEsperandoUbicacion(false);
            handleRejectLocation();
          }}
        />
      </motion.div>
    </>
  );
};

export default Login;